"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ChevronLeft, ArrowRight, Tablet } from "lucide-react";
import Link from "next/link";

export default function KioskHero() {
  const { t } = useLang();

  return (
    <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-[#FAFAF8]">
      {/* Video — base layer */}
      <video
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      >
        <source src="/videos/feature-self-kiosk.mp4" type="video/mp4" />
      </video>
      {/* Light overlay — video shows through */}
      <div className="absolute inset-0 bg-[#FAFAF8]/80 pointer-events-none" />
      <div
        className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.10) 0%, transparent 70%)" }}
      />
      <div
        className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(215,38,61,0.06) 0%, transparent 70%)" }}
      />

      <div className="relative max-w-[1280px] mx-auto">
        <Link
          href="/features"
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-redy-secondary hover:text-redy-charcoal transition-colors mb-10"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t("All features", "Todas las features")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-black tracking-widest uppercase mb-6 text-center"
          style={{ color: "#D7263D" }}
        >
          {t("Self-order kiosk · Any iPad", "Kiosko de auto-servicio · Cualquier iPad")}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-[44px] sm:text-6xl md:text-[88px] lg:text-[110px] font-black leading-[0.95] tracking-[-0.04em] mb-6 text-redy-charcoal"
        >
          {t("Spare iPad.", "iPad de sobra.")}
          <br />
          <span
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {t("Self-order ready.", "Listo para auto-servicio.")}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[17px] md:text-[20px] font-medium text-redy-secondary max-w-[680px] mx-auto leading-relaxed mb-16"
        >
          {t(
            "That iPad on the back counter? Plug it in, stand it up, it's a kiosk. Customers order, pay, and the ticket lands on your KDS. Dedicated kiosk hardware adds up fast — use the iPad you already own.",
            "¿Ese iPad del mostrador? Lo enchufas, lo paras, y es un kiosko. Los clientes ordenan, pagan, y el ticket cae en tu KDS. El hardware de kiosko dedicado suma rápido — usa el iPad que ya tienes."
          )}
        </motion.p>

        {/* Hero showcase: vertical iPad kiosk mockup */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="relative mb-12"
        >
          <div className="flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-16 max-w-[1100px] mx-auto">
            {/* iPad vertical kiosk frame */}
            <div className="relative">
              <div
                className="absolute -inset-8 rounded-full pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(244,114,43,0.18) 0%, transparent 65%)" }}
              />
              <IPadVerticalFrame>
                <div className="h-full flex flex-col bg-white text-redy-charcoal">
                  {/* Attract hero */}
                  <div
                    className="h-[180px] relative overflow-hidden flex items-end p-5"
                    style={{
                      background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                    }}
                  >
                    <div
                      className="absolute inset-0 opacity-30"
                      style={{
                        backgroundImage:
                          "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 0%, transparent 50%)",
                      }}
                    />
                    <div className="relative">
                      <div className="text-[9px] font-bold uppercase tracking-widest text-white/80">
                        {t("Welcome to", "Bienvenido a")}
                      </div>
                      <div className="text-[28px] font-black text-white leading-none tracking-tight">
                        Pepitos
                      </div>
                      <div className="text-[10px] font-bold uppercase tracking-wider mt-1 text-white/85">
                        {t("Tap to order", "Toca para ordenar")}
                      </div>
                    </div>
                  </div>

                  {/* Category tabs */}
                  <div className="px-3 py-2 flex gap-1.5 border-b border-black/5 bg-[#FAFAF8]">
                    {[
                      { name: "Tacos", active: true },
                      { name: "Drinks", active: false },
                      { name: "Sides", active: false },
                    ].map((c) => (
                      <div
                        key={c.name}
                        className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full ${
                          c.active ? "text-white" : "text-redy-secondary"
                        }`}
                        style={c.active ? { backgroundColor: "#F4722B" } : undefined}
                      >
                        {c.name}
                      </div>
                    ))}
                  </div>

                  {/* Menu grid */}
                  <div className="px-3 py-3 grid grid-cols-2 gap-2 flex-1">
                    {[
                      { name: "Birria", price: "$4.50", color: "bg-redy-orange/10" },
                      { name: "Al Pastor", price: "$4.00", color: "bg-redy-red/10" },
                      { name: "Carnitas", price: "$4.00", color: "bg-redy-orange/10" },
                      { name: "Quesabirria", price: "$6.00", color: "bg-redy-red/10" },
                    ].map((item, i) => (
                      <motion.div
                        key={item.name}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8 + 0.1 * i, duration: 0.4 }}
                        className={`${item.color} rounded-[10px] p-2 flex flex-col justify-end h-[64px] border border-black/5`}
                      >
                        <div className="text-[10px] font-black text-redy-charcoal leading-tight">{item.name}</div>
                        <div className="text-[10px] font-bold text-redy-secondary tabular-nums">{item.price}</div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Cart bar */}
                  <div
                    className="px-3 py-3 text-white text-center text-[11px] font-bold"
                    style={{ background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)" }}
                  >
                    {t("View cart · 2 items · $9.50 →", "Ver carrito · 2 items · $9.50 →")}
                  </div>
                </div>
              </IPadVerticalFrame>
            </div>

            {/* Savings card */}
            <div className="w-full max-w-[340px]">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.7 }}
                className="bg-white border border-black/5 rounded-[24px] p-7 shadow-[0_30px_80px_rgba(0,0,0,0.06)]"
              >
                <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-5">
                  {t("Per kiosk lane · example", "Por kiosko · ejemplo")}
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-redy-secondary/70 mb-1">
                      {t("Dedicated kiosk hardware (example)", "Hardware de kiosko dedicado (ejemplo)")}
                    </div>
                    <div className="text-[36px] font-black text-redy-charcoal/40 tabular-nums leading-none line-through decoration-2">
                      $1,500
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-wider mb-1" style={{ color: "#D7263D" }}>
                      {t("Redy kiosk fee", "Fee de kiosko de Redy")}
                    </div>
                    <div
                      className="text-[48px] font-black tabular-nums leading-none"
                      style={{
                        background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                        WebkitBackgroundClip: "text",
                        backgroundClip: "text",
                        color: "transparent",
                      }}
                    >
                      $0
                    </div>
                    <div className="text-[11px] font-bold mt-1 text-redy-secondary">
                      {t("Any iPad you already own.", "Cualquier iPad que ya tengas.")}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-black/5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-redy-secondary/60 mb-1">
                    {t("3 kiosk lanes, in this example", "3 kioskos, en este ejemplo")}
                  </div>
                  <div
                    className="text-[28px] font-black tabular-nums leading-none"
                    style={{
                      background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    $4,500
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="text-center"
        >
          <Link
            href="/signup"
            className="group inline-flex items-center gap-2 text-[15px] font-bold px-8 py-4 rounded-full hover:scale-[1.03] transition-transform shadow-2xl text-white"
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              boxShadow: "0 20px 60px rgba(244, 114, 43, 0.4)",
            }}
          >
            {t("Turn an iPad into a kiosk →", "Convierte un iPad en kiosko →")}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function IPadVerticalFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-[280px] h-[560px] bg-[#0a0a0a] rounded-[28px] p-[8px] shadow-[0_40px_100px_rgba(0,0,0,0.18)] relative">
      <div className="w-full h-full rounded-[22px] overflow-hidden bg-white relative">
        {/* Camera dot */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#0a0a0a] rounded-full z-10" />
        {children}
      </div>
    </div>
  );
}
